import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import api from '../services/api';
import { EDUCATION_HIERARCHY } from '../constants/profileOptions';

const EducationTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const navigation = useNavigation();
  const [educationData, setEducationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadEducationData();
  }, [memberId]);

  const loadEducationData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/education`);
      const data = response.data?.data || response.data;
      setEducationData(data);
    } catch (error) {
      console.error('Error loading education data:', error);
      Alert.alert('Error', 'Failed to load education details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEducation = (educationId) => {
    console.log('handleDeleteEducation called with educationId:', educationId);
    
    // Use platform-specific confirmation
    if (Platform.OS === 'web') {
      // Use browser's native confirm dialog for web
      if (window.confirm('Are you sure you want to delete this education record?')) {
        console.log('Delete confirmed (web) for educationId:', educationId);
        deleteEducation(educationId);
      }
    } else {
      // Use Alert.alert for native platforms
      Alert.alert(
        'Delete Education',
        'Are you sure you want to delete this education record?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: () => {
              console.log('Delete confirmed (native) for educationId:', educationId);
              deleteEducation(educationId);
            },
          },
        ]
      );
    }
  };

  const deleteEducation = async (educationId) => {
    try {
      console.log('Deleting education with educationId:', educationId);
      const endpoint = `/api/EnhancedMember/education/${educationId}`;
      console.log('Sending DELETE to:', endpoint);
      const response = await api.delete(endpoint);
      console.log('DELETE response:', response.data);
      
      if (Platform.OS === 'web') {
        alert('Education record deleted successfully');
      } else {
        Alert.alert('Success', 'Education record deleted successfully');
      }
      
      loadEducationData(); // Reload data
    } catch (error) {
      console.error('Error deleting education:', error);
      console.error('Error response:', error.response?.data);
      
      if (Platform.OS === 'web') {
        alert(error.message || 'Failed to delete education record');
      } else {
        Alert.alert('Error', error.message || 'Failed to delete education record');
      }
    }
  };

  const renderSectionTitle = (icon, title) => (
    <View style={styles.sectionHeader}>
      <SvgIcon name={icon} size={24} color={colors.accent} />
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={styles.loadingText}>Loading education details...</Text>
      </View>
    );
  }

  const educationList = educationData?.educationalQualifications || [];
  
  // Sort education list by hierarchy (highest to lowest)
  const sortedEducationList = [...educationList].sort((a, b) => {
    const hierarchyA = EDUCATION_HIERARCHY[a.educationType] || 0;
    const hierarchyB = EDUCATION_HIERARCHY[b.educationType] || 0;
    return hierarchyB - hierarchyA; // Descending order (highest first)
  });

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        {renderSectionTitle('school-outline', 'Educational Qualifications')}
        
        {sortedEducationList.length === 0 ? (
          <View style={styles.emptyStateContainer}>
            <SvgIcon name="school-outline" size={60} color={colors.textSecondary} />
            <Text style={styles.emptyStateText}>No educational qualifications added yet</Text>
          </View>
        ) : (
          sortedEducationList.map((edu, index) => {
            // Check if this is "No Education" by education type or course value
            const isNoEducation = edu.educationType === 'no_education' || 
                                 edu.course === 'No Formal Education' ||
                                 edu.customCourse === 'No Formal Education';
            
            // Display title - show "No Education" if course is "No Formal Education"
            // Also capitalize educationType for proper display
            const displayTitle = isNoEducation && !edu.educationType 
              ? 'No Education' 
              : (edu.educationType 
                  ? edu.educationType.charAt(0).toUpperCase() + edu.educationType.slice(1).replace(/_/g, ' ')
                  : 'N/A');
            
            return (
              <View key={edu.educationId || index} style={styles.educationCard}>
                <View style={styles.educationCardHeader}>
                  <View style={styles.educationCardContent}>
                    <Text style={styles.educationCardTitle}>{displayTitle}</Text>
                    {!isNoEducation && (
                      <>
                        <Text style={styles.educationCardSubtitle}>{edu.course === 'Other' ? edu.customCourse : edu.course || 'N/A'}</Text>
                        <Text style={styles.educationCardDetail}>
                          {edu.specialization ? `${edu.specialization} • ` : ''}{edu.status || 'N/A'}
                        </Text>
                      </>
                    )}
                  </View>
              {(isOwnProfile || isMediatorProfile) && (
                <View style={styles.actionButtons}>
                  <TouchableOpacity 
                    style={styles.educationEditButton}
                    onPress={() => {
                      navigation.push('EditEducation', { 
                        memberId: memberId,
                        educationId: edu.educationId,
                        education: edu,
                        allEducations: educationList // Pass original unsorted list for validation
                      });
                    }}
                  >
                    <SvgIcon name="create-outline" size={20} color={colors.accent} />
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.educationDeleteButton}
                    onPress={() => handleDeleteEducation(edu.educationId)}
                  >
                    <SvgIcon name="trash-outline" size={20} color={colors.error} />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
            );
          })
        )}
        
        {/* Add Education Button - Always visible for users with edit permissions */}
        {(isOwnProfile || isMediatorProfile) && (
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => {
              navigation.push('EditEducation', { 
                memberId: memberId,
                educationId: null,
                education: null,
                allEducations: educationList
              });
            }}
          >
            <SvgIcon name="add-circle" size={24} color={colors.white} />
            <Text style={styles.addButtonText}>Add Education</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  scrollView: {
  },
  content: {
    padding: 20,
    paddingBottom: 100,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  loadingText: {
    color: colors.white,
    marginTop: 10,
    fontSize: 16,
  },
  noDataText: {
    fontSize: 15,
    color: colors.white,
    textAlign: 'center',
    marginTop: 20,
  },
  emptyStateContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptyStateText: {
    fontSize: 15,
    color: colors.white,
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 20,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.accent,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 20,
  },
  addButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.white,
  },
  educationCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  educationCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  educationCardContent: {
    flex: 1,
    marginRight: 8,
  },
  educationCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  educationCardSubtitle: {
    fontSize: 14,
    color: colors.white,
    marginBottom: 2,
  },
  educationCardDetail: {
    fontSize: 12,
    color: colors.white,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  educationEditButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.accent,
  },
  educationDeleteButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.error,
  },
});

export default EducationTabScreen;
